import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Plus, Trash2, Edit, Copy, Search, SlidersHorizontal, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { formatPriceLakh, vehicleTitle, formatKm } from "@/lib/format";

const STATUSES = ["available", "reserved", "sold", "draft", "inactive", "archived"];
const STATUS_COLORS = {
  available: "bg-emerald-100 text-emerald-800",
  reserved: "bg-amber-100 text-amber-800",
  sold: "bg-gray-200 text-gray-700",
  draft: "bg-blue-50 text-blue-700",
  inactive: "bg-gray-100 text-gray-500",
  archived: "bg-rose-50 text-rose-700",
};

export default function InventoryPage() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());

  const load = () => {
    setLoading(true);
    const q = new URLSearchParams();
    params.forEach((v, k) => q.set(k, v));
    q.set("limit", "200");
    api.get(`/vehicles?${q.toString()}`).then((r) => setItems(r.data.items)).finally(() => setLoading(false));
  };
  useEffect(load, [params]);

  const daysInStock = (v) => {
    const d = v.purchase_date || v.created_at;
    if (!d) return 0;
    try { return Math.floor((Date.now() - new Date(d).getTime()) / 86400000); } catch { return 0; }
  };

  const bulk = async (action) => {
    if (selected.size === 0) return;
    if (action === "delete" && !window.confirm(`Delete ${selected.size} vehicle(s)?`)) return;
    try {
      await api.post("/vehicles/bulk", { ids: Array.from(selected), action });
      toast.success("Updated"); setSelected(new Set()); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleSel = (id) => {
    const n = new Set(selected); n.has(id) ? n.delete(id) : n.add(id); setSelected(n);
  };
  const allSel = items.length > 0 && items.every((v) => selected.has(v.id));

  const setFilter = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p);
  };

  return (
    <div className="space-y-5" data-testid="admin-inventory-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display font-extrabold text-2xl text-gray-900">Inventory</h1>
          <p className="text-sm text-gray-500 mt-1">{items.length} vehicles{selected.size > 0 && ` · ${selected.size} selected`}</p>
        </div>
        <Link to="/admin/inventory/new" className="btn-primary" data-testid="admin-add-vehicle-btn"><Plus size={14} /> Add Car</Link>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-[200px] flex items-center gap-2 bg-gray-50 rounded-md px-3 py-2 border border-gray-200">
          <Search size={14} className="text-gray-400" />
          <input defaultValue={params.get("search") || ""} onChange={(e) => setFilter("search", e.target.value)} placeholder="Search make, model, stock id…" className="flex-1 bg-transparent text-sm" data-testid="inv-search" />
        </div>
        <Select value={params.get("status") || ""} onChange={(v) => setFilter("status", v)} options={[["", "All status"], ...STATUSES.map((s) => [s, s])]} testid="inv-status" />
        <Select value={params.get("fuel") || ""} onChange={(v) => setFilter("fuel", v)} options={[["", "All fuel"], ...["Petrol", "Diesel", "CNG", "Electric", "Hybrid"].map((s) => [s, s])]} />
        <Select value={params.get("transmission") || ""} onChange={(v) => setFilter("transmission", v)} options={[["", "Any trans."], ...["Manual", "Automatic", "AMT", "CVT", "DCT"].map((s) => [s, s])]} />
        <Select value={params.get("sort") || "newest"} onChange={(v) => setFilter("sort", v)} options={[["newest", "Newest first"], ["oldest", "Oldest first"], ["price_desc", "Price high → low"], ["price_asc", "Price low → high"]]} />
      </div>

      {selected.size > 0 && (
        <div className="bg-gray-900 text-white rounded-lg p-3 flex items-center gap-3 text-sm">
          <span className="font-semibold">{selected.size} selected</span>
          <button onClick={() => bulk("sold")} className="rounded px-3 py-1.5 bg-white/10 hover:bg-white/20">Mark Sold</button>
          <button onClick={() => bulk("available")} className="rounded px-3 py-1.5 bg-white/10 hover:bg-white/20">Mark Available</button>
          <button onClick={() => bulk("archived")} className="rounded px-3 py-1.5 bg-white/10 hover:bg-white/20">Archive</button>
          <button onClick={() => bulk("delete")} className="rounded px-3 py-1.5 bg-red-600 hover:bg-red-700 ml-auto">Delete</button>
          <button onClick={() => setSelected(new Set())} className="text-gray-400 hover:text-white text-xs">Clear</button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
            <tr>
              <th className="p-3 text-left"><input type="checkbox" checked={allSel} onChange={() => setSelected(allSel ? new Set() : new Set(items.map((v) => v.id)))} className="h-4 w-4 accent-[#940C2C]" /></th>
              <th className="p-3 text-left">Stock</th>
              <th className="p-3 text-left">Vehicle</th>
              <th className="p-3 text-left">KM</th>
              <th className="p-3 text-left">Price</th>
              <th className="p-3 text-left">Days</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? <tr><td colSpan={8} className="p-10 text-center text-gray-400">Loading…</td></tr>
              : items.length === 0 ? <tr><td colSpan={8} className="p-10 text-center text-gray-400">No vehicles.</td></tr>
              : items.map((v) => {
                const d = daysInStock(v);
                return (
                  <tr key={v.id} className={`hover:bg-gray-50 ${selected.has(v.id) ? "bg-[#F6E6EB]/40" : ""}`}>
                    <td className="p-3"><input type="checkbox" checked={selected.has(v.id)} onChange={() => toggleSel(v.id)} className="h-4 w-4 accent-[#940C2C]" /></td>
                    <td className="p-3 font-mono text-xs">{v.stock_id}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <img src={v.images?.[0] || "https://placehold.co/80x60"} alt="" className="w-14 h-10 rounded object-cover" />
                        <div>
                          <Link to={`/admin/inventory/${v.id}`} className="font-semibold text-gray-900 hover:text-[#940C2C]">{vehicleTitle(v)}</Link>
                          <div className="text-[11px] text-gray-500">{v.fuel} · {v.transmission} · {v.owners} owner</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-xs text-gray-700">{formatKm(v.km_driven)}</td>
                    <td className="p-3 font-bold text-[#940C2C]">{formatPriceLakh(v.price)}</td>
                    <td className="p-3"><span className={`inline-flex items-center gap-1 text-xs font-bold rounded px-2 py-0.5 ${d >= 90 ? "bg-red-100 text-red-800" : d >= 60 ? "bg-orange-100 text-orange-800" : d >= 45 ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-700"}`}><Clock size={11} />{d}d</span></td>
                    <td className="p-3">
                      <select value={v.status} onChange={async (e) => { await api.patch(`/vehicles/${v.id}`, { status: e.target.value }); load(); }}
                        className={`rounded px-2 py-1 text-xs font-semibold border-0 ${STATUS_COLORS[v.status] || "bg-gray-100"}`}>
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-1">
                        <Link to={`/admin/inventory/${v.id}`} className="p-1.5 rounded hover:bg-gray-100 text-gray-600" title="Edit"><Edit size={14} /></Link>
                        <button onClick={async () => {
                          const copy = { ...v }; delete copy.id; delete copy._id; delete copy.stock_id; delete copy.slug; delete copy.activity;
                          const { data } = await api.post("/vehicles", copy);
                          toast.success("Duplicated"); nav(`/admin/inventory/${data.id}`);
                        }} className="p-1.5 rounded hover:bg-gray-100 text-gray-600" title="Duplicate"><Copy size={14} /></button>
                        <button onClick={async () => { if (window.confirm("Delete this vehicle?")) { await api.delete(`/vehicles/${v.id}`); toast.success("Deleted"); load(); } }} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Select = ({ value, onChange, options, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className="rounded-md border border-gray-200 px-3 py-2 text-sm bg-white" data-testid={testid}>
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);
